import { createContext, useContext } from 'react';
import type { PropsWithChildren } from 'react';

import { DEFAULT_RETRY_OPTIONS } from './const';
import { mergeRetryOptions, getLogoutAbortController } from './request/util';
import type { RetryOptions } from './types';

export interface ApiContextValueResolved {
  baseUrl: string;
  headers: Record<string, string>;
  defaultRequestOptions: RequestInit;
  defaultRetryOptions: RetryOptions;
}

type PossiblyUnresolvedValue<T> = T | (() => T);

export type ApiContextValueUnresolved = {
  [K in keyof ApiContextValueResolved]: PossiblyUnresolvedValue<
    ApiContextValueResolved[K]
  >;
};

export const ApiContext = createContext<ApiContextValueUnresolved>({
  baseUrl: '',
  headers: {},
  defaultRequestOptions: {},
  defaultRetryOptions: DEFAULT_RETRY_OPTIONS,
});

export const ApiContextProvider = ({
  children,
  baseUrl,
  headers,
  defaultRequestOptions,
  defaultRetryOptions,
}: PropsWithChildren<ApiContextValueUnresolved>) => {
  return (
    <ApiContext.Provider
      value={{ baseUrl, headers, defaultRequestOptions, defaultRetryOptions }}
    >
      {children}
    </ApiContext.Provider>
  );
};

const getValueIsUnresolved = <T,>(
  value: PossiblyUnresolvedValue<T>,
): value is () => T => typeof value === 'function';

const resolveValue = <T,>(value: PossiblyUnresolvedValue<T>): T =>
  getValueIsUnresolved(value) ? value() : value;

function resolveValues(
  context: ApiContextValueUnresolved,
): ApiContextValueResolved;
function resolveValues(
  context: Partial<ApiContextValueUnresolved>,
): Partial<ApiContextValueResolved>;
function resolveValues(
  context: ApiContextValueUnresolved | Partial<ApiContextValueUnresolved>,
): ApiContextValueResolved | Partial<ApiContextValueResolved> {
  const { baseUrl, headers, defaultRequestOptions, defaultRetryOptions } =
    context;
  return {
    baseUrl: resolveValue(baseUrl),
    headers: resolveValue(headers),
    defaultRequestOptions: resolveValue(defaultRequestOptions),
    defaultRetryOptions: resolveValue(defaultRetryOptions),
  };
}

export const useApiContext = (
  options: Partial<ApiContextValueUnresolved>,
): ApiContextValueResolved => {
  const context = useContext(ApiContext);
  if (context === undefined) {
    throw new Error('useApiContext must be used within an ApiContextProvider');
  }

  const resolvedContext = resolveValues(context);
  const resolvedOptions = resolveValues(options);

  const logoutAbortController = getLogoutAbortController();

  return {
    baseUrl: resolvedOptions.baseUrl ?? resolvedContext.baseUrl ?? '',
    headers: {
      ...resolvedContext.headers,
      ...resolvedOptions.headers,
    },
    defaultRequestOptions: {
      ...resolvedContext.defaultRequestOptions,
      ...resolvedOptions.defaultRequestOptions,
      ...{ signal: logoutAbortController.signal },
    },
    defaultRetryOptions: mergeRetryOptions({
      baseRetryOptions: resolvedContext.defaultRetryOptions,
      retryOptionsToMerge: resolvedOptions.defaultRetryOptions,
    }),
  };
};
